import React from 'react';
import { useTranslation } from 'react-i18next';

// Money flow for a Venture Fund, rendered as a 3-layer Sankey:
//   [investors into the fund]  →  [the fund]  →  [projects it invested in]
// Left flow widths are proportional to the fund's raise amounts (money IN);
// right flows are equal width because per-deal cheque sizes aren't disclosed.

const PALETTE = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6'];

function fmtUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}
function fmtDate(d, locale = 'vi-VN') {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}
const short = (s, n = 18) => (String(s).length > n ? String(s).slice(0, n - 1) + '…' : String(s));
// Collapse name variants like "Payward" / "Payward (Kraken parent)".
const canon = (s) => String(s || '').replace(/\s*\([^)]*\)\s*$/, '').trim();

// Cubic bezier ribbon between two vertical edges at (x1,y1a..y1b) and (x2,y2a..y2b).
function ribbon(x1, y1a, y1b, x2, y2a, y2b) {
  const mx = (x1 + x2) / 2;
  return `M${x1},${y1a} C${mx},${y1a} ${mx},${y2a} ${x2},${y2a}
          L${x2},${y2b} C${mx},${y2b} ${mx},${y1b} ${x1},${y1b} Z`;
}

export default function FundFlow({ fund }) {
  const { t, i18n } = useTranslation();
  if (!fund) return null;
  const dateLocale = i18n.language === 'en' ? 'en-US' : 'vi-VN';
  const unnamedFund = t('fundFlow.unnamedFund');

  // ── Left side: aggregate investor contributions from the fund's raises.
  // Split each round's amount equally among its named investors (sizes aren't
  // disclosed). No investors → a single "Chưa công bố" bucket with the amount.
  const contrib = new Map();
  let raiseTotal = 0;
  const dates = [];
  for (const r of (fund.raises || [])) {
    const amt = Number(r.amount_usd) || 0;
    raiseTotal += amt;
    if (r.announced_date) dates.push(r.announced_date);
    // Dedupe investors within the round by canonical name first.
    const invSeen = new Set();
    const invs = [];
    for (const raw of (r.investors || [])) {
      const c = canon(raw); const k = c.toLowerCase();
      if (!c || invSeen.has(k)) continue; invSeen.add(k); invs.push(c);
    }
    if (invs.length) { for (const iv of invs) contrib.set(iv, (contrib.get(iv) || 0) + amt / invs.length); }
    else contrib.set(unnamedFund, (contrib.get(unnamedFund) || 0) + amt);
  }
  let left = [...contrib.entries()].map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
  const LEFT_MAX = 8;
  if (left.length > LEFT_MAX) {
    const rest = left.slice(LEFT_MAX - 1);
    left = left.slice(0, LEFT_MAX - 1).concat([{ label: t('fundFlow.othersInvestors', { count: rest.length }), value: rest.reduce((s, x) => s + x.value, 0) }]);
  }
  const leftTotal = left.reduce((s, x) => s + x.value, 0) || 1;

  // ── Right side: projects the fund invested in (equal weight).
  let right = (fund.investments || []).map(iv => ({ label: iv.project, sub: iv.round_type, date: iv.announced_date }));
  const RIGHT_MAX = 14;
  let rightExtra = 0;
  if (right.length > RIGHT_MAX) { rightExtra = right.length - RIGHT_MAX; right = right.slice(0, RIGHT_MAX); }

  // ── Layout.
  const W = 1000;
  const gap = 6;
  const nodeW = 12;
  const rows = Math.max(left.length, right.length, 1);
  const H = Math.max(320, rows * 34 + 40);
  const padY = 20;
  const usableH = H - padY * 2;
  const leftX = 150, cxL = W / 2 - 40, cxR = W / 2 + 40, rightX = W - 150;

  // Left node geometry (heights ∝ value).
  const leftGapTotal = gap * Math.max(0, left.length - 1);
  let ly = padY;
  const leftNodes = left.map((d, i) => {
    const h = Math.max(4, (d.value / leftTotal) * (usableH - leftGapTotal));
    const node = { ...d, y: ly, h, color: PALETTE[i % PALETTE.length] };
    ly += h + gap;
    return node;
  });

  // Right node geometry (equal heights).
  const rH = right.length ? (usableH - gap * Math.max(0, right.length - 1)) / right.length : 0;
  let ry = padY;
  const rightNodes = right.map((d, i) => {
    const node = { ...d, y: ry, h: Math.max(4, rH), color: PALETTE[i % PALETTE.length] };
    ry += rH + gap;
    return node;
  });

  // Center node spans the full usable height; inflow partitioned by value,
  // outflow partitioned equally.
  const cy = padY, cH = usableH;
  let cInY = cy; // running y for left inflow bands
  const leftFlows = leftNodes.map(n => {
    const bandH = (n.value / leftTotal) * cH;
    const f = { color: n.color, y1a: n.y, y1b: n.y + n.h, y2a: cInY, y2b: cInY + bandH };
    cInY += bandH;
    return f;
  });
  let cOutY = cy;
  const rightFlows = rightNodes.map(n => {
    const bandH = cH / (rightNodes.length || 1);
    const f = { color: n.color, y1a: cOutY, y1b: cOutY + bandH, y2a: n.y, y2b: n.y + n.h };
    cOutY += bandH;
    return f;
  });

  const dateNote = dates.length
    ? (dates.length === 1 ? `${t('fundFlow.announced')} ${fmtDate(dates[0], dateLocale)}` : `${t('fundFlow.announced')} ${fmtDate(dates.sort()[0], dateLocale)} – ${fmtDate(dates.sort().slice(-1)[0], dateLocale)}`)
    : '';

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div>
          <span style={s.fundName}>{fund.name}</span>
          <span style={s.fundMeta}>
            {' '}· {t('fundFlow.raised')} {fmtUsd(raiseTotal || fund.totalRaised)} {dateNote && `(${dateNote})`} · {t('fundFlow.investedIn')} {fund.investmentCount} {t('fundFlow.projects')}
          </span>
        </div>
      </div>
      <div style={s.cols}>
        <span style={s.colHead}>{t('fundFlow.colInvestorsIn')}</span>
        <span style={{ ...s.colHead, textAlign: 'right' }}>{t('fundFlow.colFundOut')}</span>
      </div>
      <div style={s.svgScroll}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 680, display: 'block' }}>
          {/* flows */}
          {leftFlows.map((f, i) => (
            <path key={`lf${i}`} d={ribbon(leftX + nodeW, f.y1a, f.y1b, cxL, f.y2a, f.y2b)} fill={f.color} fillOpacity="0.4">
              <title>{left[i].label} → {fund.name}: {fmtUsd(left[i].value)}</title>
            </path>
          ))}
          {rightFlows.map((f, i) => (
            <path key={`rf${i}`} d={ribbon(cxR + nodeW, f.y1a, f.y1b, rightX, f.y2a, f.y2b)} fill={f.color} fillOpacity="0.4">
              <title>{fund.name} → {right[i].label}{right[i].sub ? ` (${right[i].sub})` : ''}</title>
            </path>
          ))}
          {/* center node */}
          <rect x={cxL} y={cy} width={cxR - cxL + nodeW} height={cH} rx="3" fill="#22c55e" fillOpacity="0.85">
            <title>{fund.name}: {fmtUsd(raiseTotal || fund.totalRaised)}</title>
          </rect>
          <text x={(cxL + cxR) / 2} y={cy - 6} textAnchor="middle" style={s.centerLabel}>{short(fund.name, 24)}</text>
          {/* left nodes */}
          {leftNodes.map((n, i) => (
            <g key={`ln${i}`}>
              <rect x={leftX} y={n.y} width={nodeW} height={n.h} rx="2" fill={n.color}>
                <title>{n.label}: {fmtUsd(n.value)}</title>
              </rect>
              <text x={leftX - 6} y={n.y + n.h / 2 + 3} textAnchor="end" style={s.nodeLabel}>{short(n.label)}</text>
            </g>
          ))}
          {/* right nodes */}
          {rightNodes.map((n, i) => (
            <g key={`rn${i}`}>
              <rect x={rightX} y={n.y} width={nodeW} height={n.h} rx="2" fill={n.color}>
                <title>{n.label}{n.sub ? ` · ${n.sub}` : ''}{n.date ? ` · ${fmtDate(n.date, dateLocale)}` : ''}</title>
              </rect>
              <text x={rightX + nodeW + 6} y={n.y + n.h / 2 + 1} style={s.nodeLabel}>{short(n.label, 20)}</text>
              {n.sub && <text x={rightX + nodeW + 6} y={n.y + n.h / 2 + 12} style={s.nodeSub}>{n.sub}{n.date ? ` · ${fmtDate(n.date, dateLocale)}` : ''}</text>}
            </g>
          ))}
        </svg>
      </div>
      {rightExtra > 0 && <div style={s.moreNote}>{t('fundFlow.moreProjects', { count: rightExtra })}</div>}
      {right.length === 0 && <div style={s.moreNote}>{t('fundFlow.noInvestments')}</div>}
    </div>
  );
}

const s = {
  wrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px' },
  header: { marginBottom: 10 },
  fundName: { fontSize: 16, fontWeight: 800, color: 'var(--text)' },
  fundMeta: { fontSize: 12, color: 'var(--text-muted)' },
  cols: { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 },
  colHead: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 },
  svgScroll: { overflowX: 'auto' },
  centerLabel: { fontSize: 11, fontWeight: 800, fill: 'var(--text)' },
  nodeLabel: { fontSize: 11, fontWeight: 700, fill: 'var(--text)' },
  nodeSub: { fontSize: 9, fill: 'var(--text-dim)' },
  moreNote: { fontSize: 11, color: 'var(--text-muted)', marginTop: 8, textAlign: 'center' },
};
